import React, { useMemo, useState } from "react";
import { QRCodeSVG } from "qrcode.react";
import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import { Printer, Download } from "lucide-react";

export default function QRCard() {
  const defaultUrl = typeof window !== "undefined" ? window.location.origin : "https://abrilsuarezmakeup.com";
  const [url, setUrl] = useState(defaultUrl);
  const [target, setTarget] = useState("site"); // site | quiz | services | whatsapp

  const resolvedUrl = useMemo(() => {
    switch (target) {
      case "quiz": return `${url}/quiz`;
      case "services": return `${url}/#services`;
      case "whatsapp": return "https://wa.me/message/SG2ELRTG32LVF1";
      default: return url;
    }
  }, [url, target]);

  const downloadSvg = () => {
    const svg = document.getElementById("qr-svg");
    if (!svg) return;
    const data = new XMLSerializer().serializeToString(svg);
    const blob = new Blob([data], { type: "image/svg+xml" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `abril-suarez-qr-${target}.svg`;
    a.click();
  };

  const downloadPng = async () => {
    const svg = document.getElementById("qr-svg");
    if (!svg) return;
    const data = new XMLSerializer().serializeToString(svg);
    const img = new Image();
    img.onload = () => {
      const canvas = document.createElement("canvas");
      canvas.width = 1024;
      canvas.height = 1024;
      const ctx = canvas.getContext("2d");
      ctx.fillStyle = "#FAFAFA";
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
      const png = canvas.toDataURL("image/png");
      const a = document.createElement("a");
      a.href = png;
      a.download = `abril-suarez-qr-${target}.png`;
      a.click();
    };
    img.src = "data:image/svg+xml;base64," + btoa(unescape(encodeURIComponent(data)));
  };

  return (
    <>
      <Helmet>
        <title>QR para tarjetas · Abril Suarez</title>
      </Helmet>
      <style>{`@media print { .no-print { display: none !important; } body { background: #fff !important; } }`}</style>

      <div className="min-h-screen bg-[#F4F0EC] py-16 px-6" data-testid="qr-page">
        <div className="max-w-[1100px] mx-auto">
          <div className="no-print mb-10">
            <Link to="/admin" className="overline text-[#8B6D5E] link-underline">← Panel</Link>
            <h1 className="font-serif text-5xl tracking-tighter mt-4">Generador QR</h1>
            <p className="text-sm text-[#5C5C5C] mt-2 max-w-xl">
              Imprime o exporta el código QR para tus tarjetas de presentación y materiales impresos. Apunta a tu sitio, al quiz personalizado o directo a WhatsApp.
            </p>

            <div className="mt-8 grid sm:grid-cols-2 gap-6">
              <div>
                <label className="field-label">URL base del sitio</label>
                <input className="field" value={url} onChange={(e) => setUrl(e.target.value)} data-testid="qr-url" />
              </div>
              <div>
                <label className="field-label">Destino</label>
                <select className="field" value={target} onChange={(e) => setTarget(e.target.value)} data-testid="qr-target">
                  <option value="site">Inicio (página principal)</option>
                  <option value="quiz">Quiz personalizado</option>
                  <option value="services">Servicios</option>
                  <option value="whatsapp">WhatsApp directo</option>
                </select>
              </div>
            </div>

            <div className="mt-6 flex flex-wrap gap-3">
              <button onClick={() => window.print()} className="btn-outline" data-testid="qr-print">
                <Printer size={14} /> Imprimir
              </button>
              <button onClick={downloadPng} className="btn-primary" data-testid="qr-download-png">
                <Download size={14} /> PNG
              </button>
              <button onClick={downloadSvg} className="btn-outline" data-testid="qr-download-svg">
                <Download size={14} /> SVG
              </button>
            </div>

            <p className="mt-3 text-xs text-[#5C5C5C] break-all">→ {resolvedUrl}</p>
          </div>

          {/* Printable card */}
          <div
            className="bg-[#FAFAFA] border border-[#E8E1DA] p-12 max-w-[600px] mx-auto"
            style={{ aspectRatio: "5 / 3" }}
            data-testid="qr-card"
          >
            <div className="h-full flex items-center justify-between gap-8">
              <div className="flex-1">
                <p className="overline text-[#8B6D5E] mb-3">Maquillaje editorial</p>
                <p className="font-serif text-4xl tracking-tighter leading-[0.95]">
                  Abril<br />
                  <span className="italic text-[#8B6D5E]">Suarez</span>
                </p>
                <p className="text-xs text-[#5C5C5C] mt-6 leading-relaxed">
                  Novias · Editorial · Eventos<br />
                  Aguascalientes, México
                </p>
                <p className="text-xs text-[#5C5C5C] mt-3">@abrilsuamkp</p>
              </div>
              <div className="text-center">
                <QRCodeSVG
                  id="qr-svg"
                  value={resolvedUrl}
                  size={180}
                  bgColor="#FAFAFA"
                  fgColor="#1A1A1A"
                  level="M"
                  includeMargin={false}
                />
                <p className="overline text-[#5C5C5C] mt-3 text-[10px]">
                  {target === "quiz" ? "Quiz gratis" : target === "whatsapp" ? "WhatsApp" : "Sitio web"}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
